/**
 * The public About page. An admin writes the sections and the contact block on
 * /admin/about; everyone reads them here.
 *
 * The content rides on the site-settings record the nav and footer already
 * fetch, so this page costs no request of its own.
 */
import type { AboutContent } from "@alpha/shared";
import { emptyAbout, hasAboutContent } from "@alpha/shared";
import { useBranding } from "../lib/siteSettings";
import { useIsAdmin } from "../lib/me";
import { ContactCard, hasContact } from "../components/ContactCard";
import { ButtonLink, Card, EmptyState, PageHeader, Workbench, stagger } from "../components/ui";

export function About() {
  const branding = useBranding();
  const isAdmin = useIsAdmin();
  const about: AboutContent = branding.about ?? emptyAbout();
  const header = <PageHeader eyebrow="About" title={branding.orgName} />;

  if (!hasAboutContent(about)) {
    return (
      <div className="space-y-8">
        {header}
        <EmptyState
          title="This page hasn't been written yet"
          hint={isAdmin ? "Add a section or two so visitors know who you are." : "Check back soon."}
          action={
            isAdmin ? <ButtonLink to="/admin/about">Edit the About page</ButtonLink> : undefined
          }
          className="mx-auto max-w-md"
        />
      </div>
    );
  }

  const { sections, contact } = about;

  // Contact with nothing written around it: a rail with no column beside it is
  // not a layout, so it becomes the page.
  if (sections.length === 0) {
    return (
      <div className="space-y-8">
        {header}
        <div className="mx-auto w-full max-w-md">
          <ContactCard contact={contact} style={stagger(2)} />
        </div>
      </div>
    );
  }

  const prose = (
    <div className="space-y-4">
      {sections.map((s, i) => (
        <Card key={`${s.title}-${i}`} className="animate-rise p-5 sm:p-6" style={stagger(i + 1)}>
          <h2 className="font-display text-lg font-semibold tracking-tight text-navy-950">
            {s.title}
          </h2>
          <p className="mt-3 max-w-prose whitespace-pre-line leading-relaxed text-slate-600">
            {s.body}
          </p>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-8">
      {header}
      {/*
        Prose first in the DOM, contact after — the inverse of TournamentHome,
        and deliberately. There the rail is the page's navigation, so a screen
        reader should reach it first. Here it is a detail beneath the thing the
        visitor came to read. Without contact there is no rail to make, so the
        prose runs full width rather than leaving an empty column at xl.
      */}
      {hasContact(contact) ? (
        <Workbench aside={<ContactCard contact={contact} style={stagger(2)} />}>{prose}</Workbench>
      ) : (
        prose
      )}
    </div>
  );
}
